import { env } from '$/env'
import { Button as BaseButton } from '@components/Buttons/Base'
import { Spacer } from '@components/Spacer'
import * as Colors from '@themes/Colors'
import { useRouter } from 'next/router'
import * as React from 'react'
import { StyleSheet, View } from 'react-native'
import { useIntercom } from 'react-use-intercom'
import { useAuthContext } from '~/features/auth/components/AuthProvider'
import { useLogout } from '~/features/auth/hooks/useLogout'
import { useReferral } from '~/features/referral/useReferral'
import { NavButton } from './NavButton'
import { NavLogoButton } from './NavLogoButton'

// https://necolas.github.io/react-native-web/docs/styling/
// https://developer.mozilla.org/en-US/docs/Web/CSS/Media_Queries/Testing_media_queries

const GAP = 16

type Props = {
  unfixed?: boolean
}
export function NavigationBar(props: Props) {
  return (
    <View
      style={[styles.wrapper, props.unfixed ? { position: 'relative' } : {}]}
    >
      <Spacer key="spacer-a" noShrink width={GAP} />

      <NavBarView />

      <Spacer key="spacer-b" noShrink width={GAP} />
    </View>
  )
}

export function NavBarView() {
  const router = useRouter()
  const { user } = useAuthContext()
  const { show } = useIntercom()

  return (
    <View style={styles.container}>
      <Spacer key="spacer-x" noShrink height={GAP * (3 / 4)} />

      <NavLogoButton
        title="Purposity"
        href={router.pathname === '/home' ? '/?no_redirect=1' : '/home'}
      />

      <Spacer key="spacer-y" noShrink height={GAP * (3 / 4)} />

      <View style={styles.navButtonsContainer}>
        <NavButton
          title="Home"
          icon="home"
          isActive={router.pathname === '/home'}
          engaged={router.pathname === '/home' && router.isFallback}
          href="/home"
          styles={{ paddingVertical: 2 }}
        />
        <NavButton
          title="Stories"
          icon="newspaper"
          isActive={router.pathname.startsWith('/stor')}
          engaged={router.pathname.startsWith('/stor') && router.isFallback}
          href="/stories"
          styles={{ paddingVertical: 2 }}
        />
        <NavButton
          title="Orgs"
          icon="sitemap"
          isActive={
            router.pathname.startsWith('/organization') &&
            router.asPath !== '/organization/givemore'
          }
          engaged={
            router.pathname.startsWith('/organization') &&
            router.asPath !== '/organization/givemore' &&
            router.isFallback
          }
          href="/organizations"
          styles={{ paddingVertical: 2 }}
        />
        <NavButton
          title="Teams"
          icon="pennant"
          isActive={router.pathname.startsWith('/team')}
          engaged={router.pathname.startsWith('/team') && router.isFallback}
          href="/teams"
          styles={{ paddingVertical: 2 }}
        />
        <NavButton
          title="Give More"
          icon="party-horn"
          isActive={router.asPath === '/organization/givemore'}
          engaged={
            router.asPath === '/organization/givemore' && router.isFallback
          }
          href="/organization/givemore"
          styles={{ paddingVertical: 2 }}
        />
        {(user?.admin_role === 'purposity_admin' ||
          user?.admin_role === 'org_admin' ||
          user?.admin_role === 'organizer' ||
          user?.admin_role === 'writer') && (
            <NavButton
              title="Admin"
              icon="gear"
              href={env.NEXT_PUBLIC_PURPOSITY_ADMIN_SITE}
              isActive={false}
              engaged={false}
              styles={{ paddingVertical: 2 }}
            />
          )}
        {(() => {
          const today = new Date()
          const month = today.getMonth()
          const day = today.getDate()

          const showTree = (month === 10 && day >= 25) || month === 11

          return showTree ? (
            <NavButton
              key="tree-button"
              title="Tree"
              icon="tree-christmas"
              isActive={router.pathname.startsWith('/tree')}
              engaged={router.pathname.startsWith('/tree') && router.isFallback}
              href="/tree/2024"
              styles={{ paddingVertical: 2 }}
            />
          ) : null
        })()}
      </View>
      <NavButton
        title="Profile"
        icon="user"
        isActive={router.pathname === '/profile'}
        engaged={router.pathname === '/profile' && router.isFallback}
        href="/profile"
        styles={{ paddingVertical: 4 }}
      />
      <View style={styles.bottomSection}>
        <View style={styles.helpButtonContainer}>
          <NavButton
            title="Chat with Us"
            icon={null}
            isActive={false}
            engaged={false}
            href="#"
            onPress={() => show()}
            styles={{ paddingVertical: 2 }}
            size="small"
          />
        </View>
        <Spacer noShrink height={16} />
        <LargeAuthButton />
        <Spacer noShrink height={12} />
      </View>
    </View>
  )
}

function LargeAuthButton() {
  const { setReferral } = useReferral()
  const { isSignedIn, isLoaded } = useAuthContext()
  const { action } = useLogout()

  const router = useRouter()

  const handleClick = React.useCallback(() => {
    if (isSignedIn) {
      action()
    } else {
      setReferral(null)
      router.push('/login')
    }
  }, [action, isSignedIn, router])

  return isSignedIn ? null : (
    <View style={{ paddingVertical: 16 }}>
      <BaseButton
        title={isSignedIn ? 'Logout' : 'Login'}
        format="rounded"
        isBusy={!isLoaded}
        onClick={handleClick}
        disabled={!isLoaded}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 24,
  },
  navButtonsContainer: {},
  bottomSection: {
    marginTop: 'auto',
    paddingBottom: 16,
  },
  wrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    height: '100%',
    position: 'fixed',
  },
  helpButtonContainer: {
    alignItems: 'center',
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: Colors.altWhite,
    paddingTop: 16,
    marginLeft: 8,
  },
})
